import Loadable from 'react-loadable';
import React, { lazy, Suspense, type ComponentProps } from 'react';
import Skeleton from '../primitives/skeleton';
import type { AtlassianSwitcherProps } from '../../types';
import type { GenericSwitcherProps } from './generic-switcher';

import { fg } from '@atlaskit/platform-feature-flags/fg';

export const loadAtlassianSwitcher = (): Promise<(props: AtlassianSwitcherProps) => JSX.Element> =>
	import(
		/* webpackChunkName: "@ak-switcher-chunk-atlassian-switcher" */ './atlassian-switcher'
	).then((module) => module.default);

export const loadGenericSwitcher = (): Promise<
	(props: GenericSwitcherProps) => React.JSX.Element
> =>
	import(/* webpackChunkName: "@ak-switcher-chunk-generic-switcher" */ './generic-switcher').then(
		(module) => module.default,
	);

const AtlassianSwitcherLoaderLazyComponent = lazy(
	() =>
		import(/* webpackChunkName: "@ak-switcher-chunk-atlassian-switcher" */ './atlassian-switcher'),
);
Object.assign(AtlassianSwitcherLoaderLazyComponent, {
	displayName: 'lazy(AtlassianSwitcherLoader)',
});

const AtlassianSwitcherLoaderLoadableComponent = Loadable({
	loader: loadAtlassianSwitcher,
	loading: () => <Skeleton />,
});

function AtlassianSwitcherLoaderWithSuspense(
	props: ComponentProps<typeof AtlassianSwitcherLoaderLazyComponent>,
): React.JSX.Element {
	return (
		<Suspense fallback={<Skeleton />}>
			{React.createElement(AtlassianSwitcherLoaderLazyComponent, props)}
		</Suspense>
	);
}

export const AtlassianSwitcherLoader: React.ComponentType<AtlassianSwitcherProps> = (props) =>
	fg('nike_r19_react_loadable')
		? React.createElement(AtlassianSwitcherLoaderWithSuspense, props)
		: React.createElement(AtlassianSwitcherLoaderLoadableComponent, props);

const GenericSwitcherLoaderLazyComponent = lazy(
	() => import(/* webpackChunkName: "@ak-switcher-chunk-generic-switcher" */ './generic-switcher'),
);
Object.assign(GenericSwitcherLoaderLazyComponent, { displayName: 'lazy(GenericSwitcherLoader)' });

const GenericSwitcherLoaderLoadableComponent = Loadable({
	loader: loadGenericSwitcher,
	loading: () => <Skeleton />,
});

function GenericSwitcherLoaderWithSuspense(
	props: ComponentProps<typeof GenericSwitcherLoaderLazyComponent>,
): React.JSX.Element {
	return (
		<Suspense fallback={<Skeleton />}>
			{React.createElement(GenericSwitcherLoaderLazyComponent, props)}
		</Suspense>
	);
}

export const GenericSwitcherLoader: React.ComponentType<GenericSwitcherProps> = (props) =>
	fg('nike_r19_react_loadable')
		? React.createElement(GenericSwitcherLoaderWithSuspense, props)
		: React.createElement(GenericSwitcherLoaderLoadableComponent, props);
